import { dayjs } from '@koob/dates';
import { getDayExperience, getDayHotel } from '@koob/trip-designer';
import { getStayRoomsFilters } from '@koob/utils';
import { ProgramAccommodationTypeEnum } from '@koob/enums';
import { getExperiences } from '../../../../api/experiences';

// Turns one day of the trip into what the checkout buys: the program that
// starts on it and the hotel stay that starts on it. `context` carries the trip
// (`days`, the two product maps, `tripFilters`, `tripId`) and the booking
// actions (`setExperienceFilters`, `setFilters`, `addExperienceToBasket`).

const getBasketStayRooms = ({ days, tripFilters }, firstDayIndex) => {
  const hotelId = days[firstDayIndex]?.hotelId;
  let stayRooms = null;
  for (
    let idx = firstDayIndex;
    hotelId && days[idx]?.hotelId === hotelId && !stayRooms;
    idx++
  ) {
    stayRooms = days[idx]?.roomsComposition?.length
      ? days[idx].roomsComposition
      : null;
  }
  return getStayRoomsFilters(stayRooms, tripFilters?.composition);
};

const getHotelDayIndex = ({ days, hotelsMap }, hotel, dayIndex) => {
  let currentIndex = 0;
  let totalDays = 0;

  for (let i = 0; i < days?.length; i++) {
    const dayHotel = getDayHotel(days[i], hotelsMap);
    const currentDay = days[i];

    const matches =
      dayHotel && !currentDay?.hotelIsIncluded && dayHotel?.id === hotel?.id;

    // IMPORTANT: Check day.hotelIsIncluded, not hotel.isIncluded
    // The hotel object may have stale isIncluded value from initialization
    if (matches) {
      if (i <= dayIndex) {
        currentIndex++;
      }
      totalDays++;
    } else {
      if (i >= dayIndex) {
        break;
      }
      currentIndex = 0;
      totalDays = 0;
    }
  }

  return {
    current: currentIndex,
    total: totalDays,
  };
};

const getSelectedHotelsForProgram = ({ days, hotelsMap }, experienceId) =>
  days.reduce((acc, day) => {
    if (day.experienceId === experienceId && day.hotelId) {
      const hotel = hotelsMap?.[day.hotelId];
      if (hotel) {
        acc.push(hotel);
      }
    }
    return acc;
  }, []);

// The composer stores an option's `dayIndex` as its position in the
// trip; the basket, the checkout and POST /experiences/book all read
// it as the day *inside* the experience (`programs[dayIndex]`,
// `startAt + dayIndex days`). Rebase it on the experience's first day
// here — otherwise an experience that doesn't start on day 1 has its
// options matched against the wrong program, which flags them as
// included services, strips their price and drops them from the
// options step of the checkout.
const toProgramDayIndex = (extra, dayIndex) => ({
  ...extra,
  dayIndex: Math.max((extra?.dayIndex ?? dayIndex) - dayIndex, 0),
});

// Extras of every day the experience covers, rebased on its first day.
const collectExperienceExtras = ({ days }, experience, dayIndex) => {
  const experienceExtras = [...(days[dayIndex]?.selectedExtras ?? [])].map(
    extra => toProgramDayIndex(extra, dayIndex),
  );

  for (let i = 1; i < experience.durationDays; i++) {
    const dayExperienceId = days[dayIndex + i]?.experienceId;
    if (dayExperienceId && dayExperienceId === experience.id) {
      const dayExtras = days[dayIndex + i]?.selectedExtras ?? [];
      experienceExtras.push(
        ...dayExtras.map(extra => toProgramDayIndex(extra, dayIndex)),
      );
    }
  }

  return experienceExtras;
};

const getSelectedProgramHotels = (context, experience) => {
  const programHotels = getSelectedHotelsForProgram(context, experience?.id);

  return experience?.programs?.reduce((acc, program, index) => {
    const hotelId = programHotels?.[index]?.id;

    if (
      hotelId &&
      [
        ProgramAccommodationTypeEnum.HOTEL,
        ProgramAccommodationTypeEnum.FREE_ACCOMMODATION,
      ].includes(program.accomodationType)
    ) {
      acc.push({
        dayIndex: index,
        hotelId,
        programId: program.id,
        isIncluded: program.isIncludedAccomodation,
      });
    }

    return acc;
  }, []);
};

async function addExperienceToBasketFromDay(
  context,
  day,
  dayIndex,
  experience,
) {
  const {
    tripFilters,
    tripId,
    setExperienceFilters,
    setFilters,
    addExperienceToBasket,
  } = context;

  // Calculate the first day of the experience in the trip
  const experienceDayIndex = day?.experienceDayIndex || 1;
  const firstDayOfExperience = dayIndex - (experienceDayIndex - 1);

  const startDate = new Date(tripFilters?.departureDate);
  if (firstDayOfExperience > 0) {
    startDate.setDate(startDate.getDate() + firstDayOfExperience);
  }
  const endDate = new Date(startDate);
  const durationDays =
    experience?.durationDays >= 2 ? experience?.durationDays - 1 : 0;
  endDate.setDate(endDate.getDate() + durationDays);

  experience.filters = {
    ...experience.filters,
    experienceComposition: tripFilters?.composition,
    startAt: startDate,
    endAt: endDate,
    isForTripSearch: true,
  };

  if (tripFilters.clientFolder) {
    setExperienceFilters({
      ...experience.filters,
      clientFolder: tripFilters.clientFolder,
    });
  }

  if (!day.experienceDayIndex || day.experienceDayIndex === 1) {
    const newExp = await getExperiences({
      filters: {
        ...experience.filters,
        experiences: [experience.id],
      },
      locale: tripFilters?.locale ?? 'en',
    });
    setFilters(experience.filters);
    setExperienceFilters(experience.filters);

    const basketExperience = {
      ...experience,
      price: newExp.data[0].price,
      selectedHotels: getSelectedProgramHotels(context, experience),
      selectedExtras: collectExperienceExtras(context, experience, dayIndex),
      tripId: tripId,
      clientFolder: tripFilters?.clientFolder,
    };

    addExperienceToBasket(basketExperience, experience.filters);
  }
}

const toBasketRooms = firstDayOfGroup => {
  // Transform allRooms into selectedRooms format for checkout
  const allRooms = firstDayOfGroup?.selectedRoom?.allRooms || [];

  return allRooms.map(room => ({
    ...room,
    bed: room?.bed ?? room.bedChoices?.[0],
    supplier: room.supplier,
    travelers: room.travelers || [], // Ensure travelers array exists
    // When the rooms' requestId dies: checkout counts down to it.
    expiresAt: firstDayOfGroup?.selectedRoom?.expiresAt,
  }));
};

const toHotelBasketItem = (
  { tripFilters, tripId },
  hotel,
  firstDayOfGroup,
) => ({
  ...hotel,
  durationDays: 1,
  type: 'hotel',
  name: hotel.name ?? hotel.displayName,
  selectedExtras: [],
  imageUrl: hotel.imageUrl,
  hotelId: hotel.id,
  price: {
    price: firstDayOfGroup?.hotelPricing?.price,
    currency: firstDayOfGroup?.hotelPricing?.currency,
    promotions: [],
  },
  hotelPricing: {
    price: firstDayOfGroup?.hotelPricing?.price,
    rooms: hotel.rooms,
    currency: firstDayOfGroup?.hotelPricing?.currency,
  },
  rooms: hotel.filters.experienceComposition,
  tripId: tripId,
  clientFolder: tripFilters?.clientFolder,

  // Include selectedRooms for checkout
  selectedRooms: toBasketRooms(firstDayOfGroup),

  // Include the room data (roomName, price, roomKey) from day storage (per-group)
  data: {
    roomName: firstDayOfGroup?.selectedRoom?.roomData?.name,
    price: firstDayOfGroup?.hotelPricing?.price,
    currency: firstDayOfGroup?.hotelPricing?.currency,
    roomKey: firstDayOfGroup?.selectedRoom?.roomKey,
    selectedRoomData: firstDayOfGroup?.selectedRoom?.roomData,
    allRooms: firstDayOfGroup?.selectedRoom?.allRooms, // Include all rooms for multi-room bookings
    expiresAt: firstDayOfGroup?.selectedRoom?.expiresAt,
  },

  // CRITICAL: Add requestId for hotel booking API
  requestId: firstDayOfGroup?.selectedRoom?.requestId,
});

function addHotelToBasketFromDay(context, dayIndex, hotel) {
  const { days, tripFilters, addExperienceToBasket } = context;
  const hotelIndex = getHotelDayIndex(context, hotel, dayIndex);

  if (hotelIndex?.current !== 1) {
    return;
  }

  const startAt = dayjs(tripFilters?.departureDate)
    .add(dayIndex, 'days')
    .toDate();
  const endAt = dayjs(startAt).add(hotelIndex.total, 'days').toDate();
  hotel.filters = {
    ...hotel.filters,
    experienceComposition: getBasketStayRooms(context, dayIndex),
  };

  // Get room selection data from day (per-group storage)
  const firstDayOfGroup = days[dayIndex];

  // Only add hotel to basket if it has room selection and pricing
  if (firstDayOfGroup?.hotelPricing?.price) {
    addExperienceToBasket(toHotelBasketItem(context, hotel, firstDayOfGroup), {
      ...hotel.filters,
      startAt: startAt,
      endAt: endAt,
    });
  }
}

export async function buildBasket(context, dayIndex) {
  const { days, experiencesMap, hotelsMap } = context;
  const day = days[dayIndex];
  const experience = getDayExperience(day, experiencesMap);

  // Process experience if present
  if (experience) {
    await addExperienceToBasketFromDay(context, day, dayIndex, experience);
  }

  // Process standalone hotels (regardless of whether there's an experience)
  const dayHotel = getDayHotel(day, hotelsMap);

  // IMPORTANT: Check day.hotelIsIncluded, not hotel.isIncluded
  // The hotel object may have stale isIncluded value from initialization
  if (dayHotel && !day?.hotelIsIncluded) {
    addHotelToBasketFromDay(context, dayIndex, dayHotel);
  }
}
